import React from 'react';
import { Alert, Box } from '@mui/material';
import { useSelector } from 'react-redux';
import AppListToolbarGlobal from '@/modules/finance/components/TransactionsToolbarGlobal';
import MainListModule from '@/__core/layout/module/MainListModule';
import axiosInstance from '@/setup/axios/axiosInstance';
import MainListTransaction from '@/modules/finance/components/MainListTransaction';
import { Transaction } from '@/modules/finance/models/Transaction';

interface FinancialOverviewTransactionsTabProps {
  type: string;
  month: string;
  companyId: string;
  monthParamKey?: 'month' | 'to_month';
  transactionTypeId?: string;
  supplierId?: string;
}

const normalizeRows = (payload: any): Transaction[] => {
  const source = payload?.results || payload?.data?.results || payload?.data || payload;
  if (Array.isArray(source)) {
    return source.map((item: any, index: number) => ({
      id: String(item?.id || `row-${index}`),
      posting_date: String(item?.posting_date || ''),
      document_date: String(item?.document_date || ''),
      transaction_type: String(item?.transaction_type || item?.transaction_type_id || ''),
      transaction_type_name: String(item?.transaction_type_name || ''),
      transaction_type_category: item?.transaction_type_category || 'expense',
      actual_amount: Number(item?.actual_amount || 0),
      total_amount: Number(item?.total_amount || 0),
      wallet: String(item?.wallet || ''),
      wallet_name: String(item?.wallet_name || ''),
      note: item?.note || '',
      order: item?.order || '',
      order_name: item?.order_name || '',
      customer: item?.customer || '',
      customer_name: item?.customer_name || '',
      customer_type: item?.customer_type || '',
      employee: item?.employee || '',
      employee_name: item?.employee_name || '',
      debt_type: item?.debt_type,
      status: item?.status || 'pending',
      created_at: String(item?.created_at || ''),
      created_by: String(item?.created_by || ''),
      created_by_name: String(item?.created_by_name || ''),
      approved_at: item?.approved_at || undefined,
      approved_by: item?.approved_by || undefined,
      approved_by_name: item?.approved_by_name || undefined,
      updated_at: String(item?.updated_at || ''),
      updated_by: item?.updated_by || undefined,
      updated_by_name: item?.updated_by_name || undefined,
      is_deleted: Boolean(item?.is_deleted),
      deleted_at: item?.deleted_at || undefined,
      deleted_by: item?.deleted_by || undefined,
      deleted_by_name: item?.deleted_by_name || undefined,
      restored_at: item?.restored_at || undefined,
      restored_by: item?.restored_by || undefined,
    }));
  }

  return [];
};

export default function FinancialOverviewTransactionsTab({
  type,
  month,
  companyId,
  monthParamKey = 'to_month',
  transactionTypeId,
  supplierId,
}: FinancialOverviewTransactionsTabProps) {
  const companies = useSelector((state: any) => state.company?.companies || []);
  const [, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<Transaction[]>([]);
  const [totalAmount, setTotalAmount] = React.useState<number | null>(null);
  const [reloadTick, setReloadTick] = React.useState(0);
  const [searchInputValue, setSearchInputValue] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = React.useState(false);

  const toolbarCompanies = React.useMemo(
    () => (Array.isArray(companies) ? companies : [])
      .filter((company: any) => String(company?.id || '') === String(companyId || ''))
      .map((company: any) => ({ id: String(company.id || ''), name: String(company.name || '') })),
    [companies, companyId]
  );

  React.useEffect(() => {
    if (!companyId || !month) {
      setRows([]);
      return;
    }

    if (!transactionTypeId && !supplierId && !type) {
      setRows([]);
      return;
    }

    let isActive = true;

    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get('/finance/transactions/', {
          params: {
            ...(transactionTypeId
              ? { transaction_type_id: transactionTypeId }
              : { type }),
            ...(supplierId ? { supplier_id: supplierId } : {}),
            ...(searchQuery ? { search: searchQuery } : {}),
            [monthParamKey]: month,
            company: companyId,
              no_pagination: true,
          },
        });

        if (!isActive) {
          return;
        }
        const normalizedRows = normalizeRows(response.data);
        const total = typeof response.data?.total_amount === 'number' ? response.data.total_amount : null;
        if (total != null) {
          const summaryRow: Transaction = {
            id: '__total__',
            posting_date: 'Tổng',
            document_date: '',
            transaction_type: '',
            transaction_type_name: '',
            transaction_type_category: 'expense' as any,
            actual_amount: 0,
            total_amount: total,
            wallet: '',
            wallet_name: '',
            note: '',
            order: '',
            order_name: '',
            customer: '',
            customer_name: '',
            customer_type: '',
            employee: '',
            employee_name: '',
            debt_type: undefined,
            status: 'pending' as const,
            created_at: '',
            created_by: '',
            created_by_name: '',
            approved_at: undefined,
            approved_by: undefined,
            approved_by_name: undefined,
            updated_at: '',
            updated_by: undefined,
            updated_by_name: undefined,
            is_deleted: false,
            deleted_at: undefined,
            deleted_by: undefined,
            deleted_by_name: undefined,
            restored_at: undefined,
            restored_by: undefined,
          } as Transaction;
          normalizedRows.push(summaryRow);
        }
        setRows(normalizedRows);
        setTotalAmount(total);
      } catch (err: any) {
        if (!isActive) {
          return;
        }

        setError(err?.response?.data?.detail || err?.message || 'Không thể tải danh sách giao dịch');
        setRows([]);
        setTotalAmount(null);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isActive = false;
    };
  }, [companyId, month, type, transactionTypeId, supplierId, monthParamKey, searchQuery, reloadTick]);

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {error && <Alert severity='error' sx={{ m: 2 }}>{error}</Alert>}
      <MainListModule
        leftSidebar={null}
        toolbar={
          <AppListToolbarGlobal
            onRefresh={() => setReloadTick((current) => current + 1)}
            searchQuery={searchInputValue}
            onSearchChange={(value) => {
              setSearchInputValue(value);
              if (value === '') {
                setSearchQuery('');
              }
            }}
            onSearchSubmit={() => setSearchQuery(searchInputValue.trim())}
            selectedCount={0}
            onToggleAdvancedSearch={() => setIsAdvancedSearchOpen((current) => !current)}
            isAdvancedSearchOpen={isAdvancedSearchOpen}
            onApplyFilters={() => setSearchQuery(searchInputValue.trim())}
            canApprove={false}
            deletedItemsCount={0}
            filters={{ company: companyId || 'all' }}
            onFilterChange={() => undefined}
            companies={toolbarCompanies}
          />
        }
        dataTable={
          <MainListTransaction
            transactions={rows}
            columns={totalAmount != null ? [{ id: 'actual_amount', label: 'Thực thu/chi', visible: false }] : undefined}
          />
        }
      />
    </Box>
  );
}
